import { Button, Input } from 'antd';
import { GripVertical, Trash2 } from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReviewMask } from '../../../sdk/@types/Revisao';
import { classesDaMarcacao } from './marcacaoAparencia';

export interface MarcacoesPanelProps {
  /** Masks in ask order — array order is the order they will be asked. */
  masks: ReviewMask[];
  selected: number | null;
  onSelect: (index: number) => void;
  onLabelChange: (index: number, label: string) => void;
  /** Same semantics as the current up/down arrows: move the mask at `from` to position `to`. */
  onReorder: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}

interface MarcacaoRowProps {
  index: number;
  mask: ReviewMask;
  selected: boolean;
  onSelect: (index: number) => void;
  onLabelChange: (index: number, label: string) => void;
  onRemove: (index: number) => void;
}

/**
 * One row of `MarcacoesPanel`'s list — a private sub-component because
 * `useSortable` is a hook, and a hook can't be called from inside the
 * `.map()` callback of the component that owns the list (that would call it
 * a variable number of times per render, breaking the rules of hooks). The
 * sortable id is the row's own current array index, stringified: the array
 * only changes atomically at drop time via `onReorder`, so there's no
 * mid-gesture staleness to worry about.
 */
function MarcacaoRow({ index, mask, selected, onSelect, onLabelChange, onRemove }: MarcacaoRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(index) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-mask-index={index}
      onClick={() => onSelect(index)}
      className={
        'flex min-w-0 cursor-pointer flex-wrap items-center gap-2 rounded-input border p-2 ' +
        (selected ? 'border-select/[0.42] bg-select/[0.14]' : 'border-edge bg-surface') +
        (isDragging ? ' opacity-50' : '')
      }
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reordenar marcação ${index + 1}`}
        onClick={(event) => event.stopPropagation()}
        className={
          'flex h-6 w-6 shrink-0 touch-none select-none items-center justify-center rounded-input ' +
          'text-ink-muted hover:bg-surface-hover hover:text-ink ' +
          (isDragging ? 'cursor-grabbing' : 'cursor-grab')
        }
      >
        <GripVertical size={14} aria-hidden />
      </button>
      <span
        className={
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-input text-xs font-bold ' +
          classesDaMarcacao(index).linhaBadge
        }
      >
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <Input
          className="w-full"
          size="small"
          placeholder="Resposta escondida"
          value={mask.label}
          onChange={(e) => onLabelChange(index, e.target.value)}
          onFocus={() => onSelect(index)}
          status={mask.label.trim() ? undefined : 'warning'}
        />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          size="small"
          type="text"
          danger
          icon={<Trash2 size={14} aria-hidden />}
          aria-label={`Remover marcação ${index + 1}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
        />
      </div>
    </li>
  );
}

/**
 * The "Marcações" list: a header with the live count, one row per existing
 * mask (numbered badge, drag handle, answer field, remove), and a trailing,
 * disabled "next" row previewing the slot the next marking will take. Header
 * and list always render, even at zero masks — the count pill reads `0` and
 * the next row's badge reads `1` — unlike the inline block this replaces,
 * which hid the whole list behind `masks.length > 0`.
 *
 * Reordering is drag-and-drop (`@dnd-kit/sortable`), by pointer **or**
 * keyboard — there is no pointer-only path: focus a row's handle, Space or
 * Enter to pick up, Arrow Up/Down to move, Space or Enter to drop, Escape to
 * cancel. This file never reorders or re-selects anything itself: `onDragEnd`
 * reads `active`/`over`'s ids as the from/to indices and calls the existing
 * `onReorder`, which the parent already wires to `moveMask`/`indexAfterMove`;
 * `onDragStart` calls `onSelect` on the picked-up row so the selection is
 * already on `from` by the time the parent resolves it to `to`.
 */
export default function MarcacoesPanel({
  masks,
  selected,
  onSelect,
  onLabelChange,
  onReorder,
  onRemove,
}: MarcacoesPanelProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    onSelect(Number(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const from = Number(active.id);
    const to = Number(over.id);
    if (from !== to) onReorder(from, to);
  };

  return (
    <div className="flex flex-col gap-2" data-marcacoes-panel="">
      <div className="flex items-center gap-2 px-1">
        <span className="text-sm font-semibold text-ink">Marcações</span>
        <span
          data-marcacoes-count={masks.length}
          className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-surface-muted px-1.5 text-xs font-semibold text-ink-muted"
        >
          {masks.length}
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={masks.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-2">
            {masks.map((mask, i) => (
              <MarcacaoRow
                key={i}
                index={i}
                mask={mask}
                selected={selected === i}
                onSelect={onSelect}
                onLabelChange={onLabelChange}
                onRemove={onRemove}
              />
            ))}

            <li
              data-mask-row="next"
              className="flex min-w-0 items-center gap-2 rounded-input border border-dashed border-edge bg-surface-muted/40 p-2"
            >
              {/* Spacer matching a real row's drag handle (w-6 + gap-2), so this
                  row's badge lines up under the badges above it. */}
              <span className="h-6 w-6 shrink-0" aria-hidden />
              <span
                className={
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-input text-xs font-bold ' +
                  classesDaMarcacao(masks.length).linhaBadgeProxima
                }
              >
                {masks.length + 1}
              </span>
              <div className="min-w-0 flex-1">
                <Input className="w-full" size="small" placeholder="Digite a resposta…" disabled />
              </div>
            </li>
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
}
